import React from "react";
import styled from "styled-components";
import Select from "react-select";
import { Campo, TipoPeriodicidadePagamento } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";

interface SelectWithNumberProps {
  campo: Campo & { defaultValue?: string };
  modo: string;
  getFieldValue: (nome: string) => any;
  handleInputChange: (nome: string, value: any) => void;
  onBlur?: () => void;
  hasError?: boolean;
  apenasLeitura?: boolean;
}

interface ContainerProps {
  $hasError?: boolean;
}

export interface OpcaoComMaximo {
  valor: any;
  label: string;
  maximo?: number;
}

export const OPCOES_PRAZO_TODAS: OpcaoComMaximo[] = [
  { label: "Mês(es)", valor: TipoPeriodicidadePagamento.Mensal, maximo: 48 },
  { label: "Dia(s)", valor: TipoPeriodicidadePagamento.Diario, maximo: 180 },
  { label: "Semana(s)", valor: TipoPeriodicidadePagamento.Semanal, maximo: 156 },
  { label: "Quinzena(s)", valor: TipoPeriodicidadePagamento.Quinzenal, maximo: 78 },
];

const Container = styled.div<ContainerProps>`
  display: flex;
  width: 100%;
  border: 1px solid ${props => props.$hasError ? '#dc3545' : '#ced4da'};
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.15s ease-in-out;
  box-shadow: ${props => props.$hasError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : 'none'};

  &:focus-within {
    border-color: ${props => props.$hasError ? '#dc3545' : '#80bdff'};
    box-shadow: 0 0 0 0.2rem rgba(${props => props.$hasError ? '220, 53, 69' : '0, 123, 255'}, 0.25);
  }
`;

const NumberInput = styled.input`
  flex: 1;
  min-width: 80px;
  padding: 8px 12px;
  border: none;
  font-size: 0.95rem;
  outline: none;
  background-color: ${props => props.disabled ? '#e9ecef' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const SelectContainer = styled.div`
  width: 120px;
  border-left: 1px solid #ced4da;
`;

interface StyledSelectProps {
  $hasError?: boolean;
  disabled?: boolean;
}

const StyledSelect = styled(Select)<StyledSelectProps>`
  & .react-select__control {
    min-height: 37px;
    height: 37px;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background-color: ${props => props.disabled ? '#e9ecef' : 'white'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  }

  & .react-select__value-container {
    height: 35px;
    padding: 0 8px;
  }

  & .react-select__indicators {
    height: 35px;
  }

  & .react-select__menu {
    z-index: 9999;
  }
`;

const SelectWithNumber: React.FC<SelectWithNumberProps> = ({
  campo,
  modo,
  getFieldValue,
  handleInputChange,
  onBlur,
  hasError,
  apenasLeitura
}) => {
  // Converte as opções para o tipo com máximo
  const opcoesComMaximo = campo.opcoes as OpcaoComMaximo[] || [];
  
  const options = opcoesComMaximo.map(opcao => ({
    value: opcao.valor,
    label: opcao.label,
    maximo: opcao.maximo // Inclui o valor máximo
  }));

  const defaultValue = campo.defaultValue || 
                       (options.length > 0 ? options[0].value : '');

  const currentValue = getFieldValue(campo.nome);

  let numericValue: string = "1";
  let selectedUnit = defaultValue;

  if (typeof currentValue === 'object' && currentValue !== null) {
    numericValue = currentValue.value?.toString() ?? "";
    selectedUnit = currentValue.unit || defaultValue;
  } else if (typeof currentValue === 'string') {
    const match = currentValue.match(/(\d+)\s*(\w+)/);
    if (match) {
      numericValue = match[1];
      selectedUnit = match[2];
    }
  }

  React.useEffect(() => {
    if (options.length > 0 && !options.some(option => option.value === selectedUnit)) {
      const novaUnidade = options[0].value;
      const parsedValue = parseInt(numericValue, 10);
      
      const newValue = {
        value: isNaN(parsedValue) ? '' : parsedValue,
        unit: novaUnidade
      };
      handleInputChange(campo.nome, newValue);
    }
  }, [options, selectedUnit, numericValue, campo.nome, handleInputChange]);

  // Encontra a opção selecionada atual para obter o máximo
  const selectedOption = options.find(option => option.value === selectedUnit);
  const maxValue = selectedOption?.maximo;

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      handleInputChange(campo.nome, { value: '', unit: selectedUnit });
      return;
    }

    const parsed = parseInt(value, 10);
    
    // Verifica se o valor excede o máximo permitido
    if (!isNaN(parsed) && (!maxValue || parsed <= maxValue)) {
      const newValue = {
        value: parsed,
        unit: selectedUnit
      };
      handleInputChange(campo.nome, newValue);
      campo.onCampoDigitar && campo.onCampoDigitar(newValue);
    }
  };

  const handleUnitChange = (selectedOption: any) => {
    const unit = selectedOption?.value || defaultValue;
    const parsedValue = parseInt(numericValue, 10);

    // Encontra a nova opção selecionada para obter o máximo
    const novaOpcao = options.find(option => option.value === unit);
    const novoMaximo = novaOpcao?.maximo;

    // Ajusta o valor se exceder o novo máximo
    let valorAjustado = isNaN(parsedValue) ? '' : parsedValue;
    if (novoMaximo && parsedValue > novoMaximo) {
      valorAjustado = novoMaximo;
    }

    const newValue = {
      value: valorAjustado,
      unit: unit
    };
    handleInputChange(campo.nome, newValue);
    campo.onCampoDigitar && campo.onCampoDigitar(newValue);
  };

  const handleBlur = () => {
    onBlur && onBlur();
    const parsedValue = parseInt(numericValue, 10);

    campo.onCampoSair && campo.onCampoSair({
      value: isNaN(parsedValue) ? '' : parsedValue,
      unit: selectedUnit
    });
  };

  return (
    <Container $hasError={hasError}>
      <NumberInput
        type="number"
        min="1"
        max={maxValue} // Define o atributo max com o valor máximo
        value={numericValue}
        onChange={handleNumberChange}
        onBlur={handleBlur}
        disabled={apenasLeitura || modo === TipoFormulario.tpVisualizacao}
      />
      <SelectContainer>
        <StyledSelect
          classNamePrefix="react-select"
          options={options}
          value={options.find(option => option.value === selectedUnit)}
          onChange={handleUnitChange}
          onBlur={handleBlur}
          isDisabled={apenasLeitura || modo === TipoFormulario.tpVisualizacao}
          placeholder=""
          menuPortalTarget={document.body}
          menuPlacement="auto"
          $hasError={hasError}
          styles={{
            menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 }),
            menu: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 })
          }}
        />
      </SelectContainer>
    </Container>
  );
};

export default SelectWithNumber;